import React, { useCallback, useEffect, useState } from 'react';
import { Copy, RefreshCcw } from 'lucide-react';
import { Language, Theme } from '../types';
import { supabase } from '../services/supabaseClient';

interface ApiKeyScreenProps {
  theme: Theme;
  language: Language;
  t: any;
  onBack: () => void;
}

type ApiKeyResponse = {
  ok?: boolean;
  api_key?: string | null;
  api_key_masked?: string | null;
  key_prefix?: string | null;
  plan?: number | null;
  credits_remaining?: number | null;
  credits_reset_at?: string | null;
  plan_expires_at?: string | null;
};

const planLabel = (plan: number | null | undefined, t: any) => {
  if (plan === 2) return t.pricingStarterName;
  if (plan === 3) return t.pricingProName;
  if (plan === 4) return t.pricingEnterpriseName;
  return t.freePlanName;
};

export const ApiKeyScreen: React.FC<ApiKeyScreenProps> = ({ theme, language, t, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [maskedKey, setMaskedKey] = useState<string>('••••••••');
  const [isFreshlyRegenerated, setIsFreshlyRegenerated] = useState(false);
  const [creditsRemaining, setCreditsRemaining] = useState<number | null>(null);
  const [plan, setPlan] = useState<number | null>(1);
  const [creditsResetAt, setCreditsResetAt] = useState<string | null>(null);
  const [planExpiresAt, setPlanExpiresAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isRotateModalOpen, setIsRotateModalOpen] = useState(false);

  const isDark = theme === 'dark';
  const skeletonBlock = isDark ? 'bg-slate-800/70' : 'bg-slate-200';
  const goToPlan = () => window.location.assign('/app/plan');

  const getAccessToken = async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    let token = sessionData.session?.access_token ?? null;
    if (!token) {
      const { data: refreshed } = await supabase.auth.refreshSession();
      token = refreshed.session?.access_token ?? null;
    }
    return token;
  };

  const fetchKey = useCallback(
    async (reveal: boolean, initPlan?: number) => {
      setLoading(true);
      setError(null);
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError(t.apiKeyNeedAuth);
        setHasLoaded(true);
        setLoading(false);
        return;
      }
      const body: Record<string, any> = { action: 'get', reveal };
      if (initPlan) {
        body.plan = initPlan;
        body.action = 'init';
      }
      const { data, error: fnError } = await supabase.functions.invoke('api-key', {
        body,
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (fnError) {
        setError(fnError.status === 401
          ? t.sessionExpiredError
          : t.apiKeyLoadError);
        setHasLoaded(true);
        setLoading(false);
        return;
      }
      const payload = data as ApiKeyResponse;
      setApiKey(payload.api_key ?? null);
      setMaskedKey(payload.api_key_masked ?? '••••••••');
      setIsFreshlyRegenerated(false);
      setCopied(false);
      setPlan(payload.plan ?? 1);
      setCreditsRemaining(payload.credits_remaining ?? null);
      setCreditsResetAt(payload.credits_reset_at ?? null);
      setPlanExpiresAt(payload.plan_expires_at ?? null);
      setHasLoaded(true);
      setLoading(false);
    },
    []
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const planParam = params.get('plan');
    const storedPlan = sessionStorage.getItem('pendingPlan') || localStorage.getItem('pendingPlan');
    const planValue = planParam ? Number(planParam) : storedPlan ? Number(storedPlan) : null;
    if (planValue && [1, 2, 3, 4].includes(planValue)) {
      fetchKey(false, planValue);
      sessionStorage.removeItem('pendingPlan');
      localStorage.removeItem('pendingPlan');
      const cleanUrl = window.location.pathname;
      window.history.replaceState(null, '', cleanUrl);
    } else {
      fetchKey(false);
    }
  }, [fetchKey]);

  const handleCopy = async () => {
    if (!apiKey || !isFreshlyRegenerated) {
      setError(t.apiKeyCopyError);
      return;
    }
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError(t.apiKeyCopyError);
    }
  };

  const handleRotate = async () => {
    setLoading(true);
    setError(null);
    const accessToken = await getAccessToken();
    if (!accessToken) {
      setError(t.apiKeyRotateNeedAuth);
      setHasLoaded(true);
      setLoading(false);
      return;
    }
    const { data, error: fnError } = await supabase.functions.invoke('api-key', {
      body: { action: 'rotate', reveal: true },
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    if (fnError) {
      setError(fnError.status === 401
        ? t.sessionExpiredError
        : t.apiKeyRotateError);
      setHasLoaded(true);
      setLoading(false);
      return;
    }
    const payload = data as ApiKeyResponse;
    setApiKey(payload.api_key ?? null);
    setMaskedKey(payload.api_key_masked ?? '••••••••');
    setPlan(payload.plan ?? plan);
    setCreditsRemaining(payload.credits_remaining ?? creditsRemaining);
    setCreditsResetAt(payload.credits_reset_at ?? creditsResetAt);
    setPlanExpiresAt(payload.plan_expires_at ?? planExpiresAt);
    setCopied(false);
    setIsFreshlyRegenerated(Boolean(payload.api_key));
    setLoading(false);
  };

  const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US') : '—';
  const showSkeleton = loading && !hasLoaded;
  const displayKey = isFreshlyRegenerated && apiKey ? apiKey : maskedKey;

  return (
    <div className="max-w-5xl mx-auto px-4 pb-8">
      <div className="flex items-center justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={onBack}
            className={`inline-flex items-center gap-2 text-xs uppercase tracking-widest ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
          >
            <span aria-hidden="true">←</span>
            {t.accountBack}
          </button>
          <h1 className={`mt-3 text-2xl font-semibold sm:text-3xl ${isDark ? 'text-white' : 'text-slate-900'}`}>{t.apiKeyTitle}</h1>
        </div>
      </div>

      <div className="mt-10 grid gap-6">
        {showSkeleton ? (
          <>
            <section className={`rounded-3xl border p-4 sm:p-6 ${isDark ? 'border-white/10 bg-slate-900/60' : 'border-slate-200 bg-white'}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-3">
                  <div className={`h-5 w-32 rounded-full ${skeletonBlock} animate-pulse`} />
                  <div className={`h-4 w-60 rounded-full ${skeletonBlock} animate-pulse`} />
                </div>
                <div className={`h-9 w-28 rounded-full ${skeletonBlock} animate-pulse`} />
              </div>
              <div className="mt-6 space-y-4">
                <div className={`h-12 w-full rounded-2xl ${skeletonBlock} animate-pulse`} />
              </div>
            </section>

          </>
        ) : (
          <>
            <section className={`rounded-3xl border p-4 sm:p-6 ${isDark ? 'border-white/10 bg-slate-900/60' : 'border-slate-200 bg-white'}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{t.apiKeyCardTitle}</h2>
                  <p className={`mt-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    {t.apiKeyCardDesc}
                  </p>
                  <p className={`mt-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    {t.apiKeySecurityNote}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsRotateModalOpen(true)}
                  className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-[11px] font-semibold transition sm:px-4 sm:text-xs ${isDark ? 'bg-white text-slate-900 hover:bg-slate-200' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
                >
                  <RefreshCcw className="h-4 w-4" />
                  {t.apiKeyRotate}
                </button>
              </div>

          <div className="mt-6 flex flex-col gap-5">
            <div className={`rounded-2xl border px-4 py-3 flex min-w-0 flex-col gap-3 ${isDark ? 'border-white/10 bg-slate-950' : 'border-slate-200 bg-slate-50'}`}>
              <div className="min-w-0 pr-2">
                <p className="text-[10px] uppercase tracking-widest text-slate-500">{t.apiKeyValueLabel}</p>
                <div className="relative mt-1">
                  <input
                    type="text"
                    readOnly
                    value={displayKey}
                    className={`w-full bg-transparent pr-10 font-mono text-[11px] tracking-normal break-all sm:text-xs sm:tracking-wide md:text-sm md:tracking-widest outline-none ${isDark ? 'text-slate-200' : 'text-slate-700'}`}
                    aria-label={t.apiKeyValueLabel}
                  />
                  {isFreshlyRegenerated && apiKey && (
                    <button
                      type="button"
                      onClick={handleCopy}
                      className={`absolute right-0 top-1/2 -translate-y-1/2 rounded-full p-2 transition ${isDark ? 'text-slate-300 hover:bg-white/10' : 'text-slate-600 hover:bg-slate-200'}`}
                      aria-label={t.apiKeyCopyAria}
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
                </div>
                {copied && <p className="text-xs text-emerald-400">{t.apiKeyCopied}</p>}
                {error && <p className="text-xs text-rose-400">{error}</p>}
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-3">
                <button
                  type="button"
                  onClick={goToPlan}
                  className={`rounded-2xl border px-4 py-3 sm:py-4 text-left transition ${
                    isDark ? 'border-white/10 bg-slate-950 hover:bg-slate-900' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                  }`}
                >
                  <p className="text-xs uppercase tracking-widest text-slate-500">{t.planTypeLabel}</p>
                  <p className={`mt-2 text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{planLabel(plan, t)}</p>
                </button>
                <button
                  type="button"
                  onClick={goToPlan}
                  className={`rounded-2xl border px-4 py-3 sm:py-4 text-left transition ${
                    isDark ? 'border-white/10 bg-slate-950 hover:bg-slate-900' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                  }`}
                >
                  <p className="text-xs uppercase tracking-widest text-slate-500">{t.creditsLabel}</p>
                  <p className={`mt-2 text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{creditsRemaining ?? '—'}</p>
                </button>
                <div className={`rounded-2xl border px-4 py-3 sm:py-4 ${isDark ? 'border-white/10 bg-slate-950' : 'border-slate-200 bg-slate-50'}`}>
                  <p className="text-xs uppercase tracking-widest text-slate-500">{t.creditsRenewalLabel}</p>
                  <p className={`mt-2 text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {formatDate(creditsResetAt ?? planExpiresAt)}
                  </p>
                </div>
              </div>
            </section>

            
          </>
        )}
      </div>
      {isRotateModalOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsRotateModalOpen(false)}
            aria-hidden="true"
          />
          <div className={`relative w-full max-w-lg rounded-3xl border p-5 shadow-2xl sm:p-6 ${isDark ? 'bg-black border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
            <h3 className="text-lg font-semibold sm:text-xl">{t.apiKeyRotateModalTitle}</h3>
            <p className={`mt-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {t.apiKeyRotateModalBody}
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsRotateModalOpen(false)}
                className={`rounded-full px-4 py-2 text-[11px] font-semibold transition sm:text-xs ${isDark ? 'border border-white/20 text-white hover:bg-white/10' : 'border border-slate-200 text-slate-700 hover:bg-slate-100'}`}
              >
                {t.contactCancel}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsRotateModalOpen(false);
                  handleRotate();
                }}
                className={`rounded-full px-4 py-2 text-[11px] font-semibold transition sm:text-xs ${isDark ? 'bg-white text-slate-900 hover:bg-slate-200' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
              >
                {t.apiKeyRotateConfirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
